import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import useIntersectionObserver from 'hooks/useIntersectionObserver'

export type UseIntersectionObserverLoadMoreParams = {
  /** Scroll container the sentinel is observed within (e.g. modal body `overflow-y-auto`). */
  rootScroll: Element | null
  /** When false, observation is off (e.g. modal/sheet closed). */
  active: boolean
  hasMore: boolean
  isLoading: boolean
  loadMore?: () => void | Promise<void>
  /** Passed to IntersectionObserver; prefetch before sentinel hits viewport edge. */
  rootMargin?: string
}

/**
 * Attach the returned ref to a sentinel node at the bottom of a scrollable list; when it enters
 * view, `loadMore` runs once per visibility transition (not on every frame while intersecting).
 */
export function useIntersectionObserverLoadMore({
  rootScroll,
  active,
  hasMore,
  isLoading,
  loadMore,
  rootMargin = '100px',
}: UseIntersectionObserverLoadMoreParams): {
  sentinelRef: RefObject<HTMLDivElement>
} {
  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver({
    root: rootScroll,
    rootMargin,
    triggerOnce: false,
    enabled: active && Boolean(rootScroll && hasMore && !isLoading && loadMore),
  })

  const wasIntersectingRef = useRef(false)

  useEffect(() => {
    if (!active) {
      wasIntersectingRef.current = false
    }
  }, [active, rootScroll])

  useEffect(() => {
    const justBecameVisible = isIntersecting && !wasIntersectingRef.current
    wasIntersectingRef.current = isIntersecting

    if (justBecameVisible && hasMore && !isLoading && loadMore) {
      void loadMore()
    }
  }, [isIntersecting, hasMore, isLoading, loadMore])

  return { sentinelRef }
}
